<template>
  <!-- Figma D2 "Select Parts (Drawer)": search, part rows with a check (selected = blue), round pagination.
       Same selection / search / paging logic as before. -->
  <div v-if="selectedItem" class="side--modal font-roboto mobile:flex laptop:block flex-col gap-[10px]">
    <label
      class="flex items-center gap-[8px] mobile:h-[44px] laptop:h-[38px] m-0 mb-[10px] mobile:px-[16px] laptop:px-[12px] mobile:rounded-full laptop:rounded-[8px] border-1 border-[#E9ECF1] bg-white">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="11" cy="11" r="7" stroke="#A8AEB8" stroke-width="2" />
        <path d="M20 20l-3.5-3.5" stroke="#A8AEB8" stroke-width="2" stroke-linecap="round" />
      </svg>
      <input v-model="search" type="text" placeholder="Enter Part name to search..."
        class="flex-1 min-w-0 border-0 outline-none bg-transparent text-[13px] text-[#333333] placeholder:text-[#A8AEB8]"
        @input="onSearch">
    </label>
    <div class="listCheckbox">
      <ul class="list-inline m-0 flex flex-col gap-[6px] !pr-[5px] max-h-[330px] overflow-y-auto">
        <li v-for="(item, idx) in filteredList" :key="idx">
          <label :for="item.part_id + '-' + index"
            class="flex items-center justify-between gap-[8px] m-0 px-[12px] py-[9px] rounded-[6px] border-1 cursor-pointer text-[13px]"
            :class="isChecked(item) ? 'border-[#7695FF] bg-[#EEF2FF] text-[#7695FF]' : 'border-[#EFEFEF] bg-white text-[#333333]'">
            <input :id="item.part_id + '-' + index" type="checkbox" class="sr-only" :checked="isChecked(item)"
              @change="toggleSelection(item)">
            <span class="min-w-0 break-words">{{ item.part_name }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none" />
              <path fill="none" :stroke="isChecked(item) ? '#7695FF' : '#CCCCCC'" stroke-linecap="round"
                stroke-linejoin="round" stroke-width="2"
                d="m8 12.485l4.243 4.243l8.484-8.485M3 12.485l4.243 4.243m8.485-8.485L12.5 11.5" />
            </svg>
          </label>
        </li>
        <li v-if="!isLoading && !filteredList?.length" class="py-[20px] text-center text-[13px] text-[#8A9099]">No parts
          found</li>
      </ul>
    </div>

    <div v-if="total_pages > 1" class="flex items-center justify-center gap-[6px] mt-[15px]">
      <button type="button"
        class="w-[26px] h-[26px] rounded-full border-0 bg-[#A084E8] text-white disabled:opacity-40"
        :disabled="page <= 1" aria-label="Previous page" @click="page--">‹</button>
      <button v-for="p in visiblePages" :key="p" type="button" class="w-[26px] h-[26px] rounded-full text-[12px]"
        :class="p === page ? 'bg-[#A084E8] text-white border-0' : 'bg-white border-1 border-[#E9ECF1] text-[#666666]'"
        :disabled="p === '…'" @click="p !== '…' && (page = p)">{{ p }}</button>
      <button type="button" class="w-[26px] h-[26px] rounded-full border-0 bg-[#A084E8] text-white disabled:opacity-40"
        :disabled="page >= total_pages" aria-label="Next page" @click="page++">›</button>
    </div>
  </div>
</template>

<script>

import { mapActions, mapGetters } from "vuex";

export default {

  components: {},
  props: ["item", "index"],

  data() {
    return {
      pageLimit: 100,
      search: "",
      page: 1,
      selectedItem: null,
      jobId: this.$route.params.id,
      total_pages: 0,
      partsList: [],
      debounceTimer: null,
      isLoading: true,
    };
  },

  computed: {
    ...mapGetters([
      "bookingResponse",
      "bookingParts",
    ]),

    pages() {
      return this.total_pages
    },

    filteredList() {
      return this.partsList
    },

    // 1 2 3 4 … last (Figma pagination)
    visiblePages() {
      const total = this.total_pages;
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
      const start = Math.max(1, Math.min(this.page - 1, total - 3));
      const pages = [start, start + 1, start + 2, start + 3].filter(p => p < total);
      return [...pages, '…', total];
    },
  },

  watch: {

    bookingParts: {
      handler: function (value, newValue) {
        this.isLoading = false
        clearTimeout(this.debounceTimer);
        this.setScroll()
        this.partsList = this.bookingParts.body
        this.total_pages = this.bookingParts.paging.total_pages
      }
    },

    page: {
      handler: function (value, newValue) {
        this.getPartsCall()
      }
    },

    item: function () {

      this.page = 1
      this.total_pages = 0
      this.partsList = []
      this.search = ""
      this.selectedItem = this.item;
      this.getPartsCall()
    },

  },

  mounted() {
    this.search = ""
  },

  methods: {
    ...mapActions([
      "getBookingParts",
    ]),

    onSearch() {
      clearTimeout(this.debounceTimer);
      // Start new debounce timer
      this.page = 1
      this.debounceTimer = setTimeout(() => {
        if (this.search.length == 0 || this.search.trim().length > 0) {
          this.getPartsCall()
        }
      }, 1000); // 1000ms delay
    },

    isChecked(item) {
      return this.selectedItem.parts.some(part => part.part_id === item.part_id);
    },

    toggleSelection(item) {

      if (this.bookingResponse.is_flat_price_model) {
        this.selectedItem.parts = []
        this.selectedItem.parts.push(JSON.parse(JSON.stringify(item))); // Add item

      } else {
        const index = this.selectedItem.parts.findIndex(part => part.part_id === item.part_id);
        if (index > -1) {
          this.selectedItem.parts.splice(index, 1); // Remove item
        } else {
          this.selectedItem.parts.push(JSON.parse(JSON.stringify(item))); // Add item
        }
      }

    },

    setScroll() {
      const ulElement = document.querySelector('.listCheckbox ul');
      if (ulElement) {
        ulElement.scrollTop = 0; // Scrolls to the top
      }
    },

    getPartsCall() {
      this.isLoading = true
      this.getBookingParts({
        "page": this.page,
        "limit": this.pageLimit,
        "search": this.search,
        category_id: "0",
        booking_id: this.jobId,
      });
    },

    clearSearchClicked() {
      this.search = ""
      this.page = 1
      this.getPartsCall()
    },


  },
};
</script>


<style>
.side--modal .Pagination {
  justify-content: center;
  margin: 1rem 0 20px 0;
}

.selectPartSidebar .PaginationControl,
.selectPartSidebar .Page {
  border: 1px solid #ccc;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 1px !important;
  line-height: 0;
}

.selectPartSidebar .Page-active {
  width: 30px;
  height: 30px;
  border: 1px solid #fe5000;
  color: #fff;
}

.buttonsList {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  padding: 0.5rem;
  line-height: 1;
}

.buttonsList span {
  width: 18px;
  height: 18px;
  text-align: center;
  color: #fff;
}

@media (max-width: 1200px) {
  .selectPartSidebar .Pagination {
    justify-content: center;
    margin: 1rem 0;
  }
}
</style>
